import { useCallback, useMemo, useState } from 'react';

import { Native } from '@givebacks/api';
import { useUserAccessPermissions } from '@givebacks/api/core';
import { Constants, usePlatformService, usePlatformShare } from 'core';
import { ContactOrganizerModal, ShareModal } from 'core/Components';
import { BASE_URL, customSubDomain } from 'core/Utils';
import { ActionIcon, KebabMenu } from 'ui';
import { IconDashboard, IconMail, IconShare } from 'ui/Icons';

import { useCausePageContext } from '../../CauseContext';

const CausePageHeaderActions = ({ color, hasStore }: { color: string; hasStore: boolean }) => {
  const { cause } = useCausePageContext();
  const platform = usePlatformService();
  const { platformShareCause } = usePlatformShare();

  const { data: userAccessCausePermissions } = useUserAccessPermissions({
    accessType: 'cause',
    causeId: cause.uuid,
  });

  const [modalOpen, setModalOpen] = useState<'share' | 'contact' | null>(null);

  const hasAdminDashboardAccess = useMemo(() => {
    return userAccessCausePermissions?.write || userAccessCausePermissions?.read;
  }, [userAccessCausePermissions?.read, userAccessCausePermissions?.write]);

  const showShareOnly = useMemo(() => {
    return !hasStore && !hasAdminDashboardAccess;
  }, [hasStore, hasAdminDashboardAccess]);

  const causePageLink = useMemo(
    () => `${BASE_URL}${Constants.appRoutes.cause(cause.shortener ?? cause.uuid ?? '')}`,
    [cause.shortener, cause.uuid]
  );

  const shareAction = useCallback(() => {
    void platformShareCause({ url: causePageLink, causeName: cause.name ?? 'Your Cause' }, () =>
      setModalOpen('share')
    );
  }, [platformShareCause, causePageLink, cause.name]);

  const openAdminDashboard = useCallback(() => {
    const url = `${customSubDomain(cause.shortener ?? cause.uuid)}/dashboard`;
    if (platform.isNative) {
      Native.postURL(url);
    } else {
      window.open(url, '_blank');
    }
  }, [cause.shortener, cause.uuid, platform.isNative]);

  const kebabMenuItems = useMemo(() => {
    const items = [
      {
        label: 'Share',
        icon: <IconShare />,
        onClick: shareAction,
      },
    ];

    if (hasStore) {
      items.push({
        label: 'Contact Seller',
        icon: <IconMail />,
        onClick: () => {
          setModalOpen('contact');
        },
      });
    }

    if (hasAdminDashboardAccess) {
      items.push({
        label: 'Open Admin Dashboard',
        icon: <IconDashboard />,
        onClick: openAdminDashboard,
      });
    }

    return items;
  }, [hasStore, hasAdminDashboardAccess, shareAction, openAdminDashboard]);

  return (
    <>
      {showShareOnly ? (
        <ActionIcon
          onClick={shareAction}
          color={color}
          variant="subtle"
          size="md"
          bg="rgba(255, 255, 255, 0.12)"
        >
          <IconShare />
        </ActionIcon>
      ) : (
        <KebabMenu
          buttonProps={{
            variant: 'subtle',
            size: 'md',
            color,
            bg: 'rgba(255, 255, 255, 0.12)',
          }}
          items={kebabMenuItems}
        />
      )}
      <ShareModal
        opened={modalOpen === 'share'}
        onClose={() => setModalOpen(null)}
        causeName={cause.name}
        link={causePageLink}
        imgSrc={cause.logo?.url}
      />
      <ContactOrganizerModal
        title="Contact Seller"
        causeId={cause.uuid}
        opened={modalOpen === 'contact'}
        onClose={() => setModalOpen(null)}
      />
    </>
  );
};

export { CausePageHeaderActions };
